import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiSave,
  FiSettings,
} from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import {
  createSettingProfile,
  updateSettingProfile,
  deleteSettingProfile,
  getSettingProfilesByClient,
} from "../api/settingProfileApi";
import { toast } from "react-toastify";
import ControllerSettingsForm from "../components/SettingsProfileForm";

const ManageSettingProfiles = () => {
  const { clientData } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [showConfigForm, setShowConfigForm] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },getValues,
  } = useForm();

  useEffect(() => {
    const loadProfiles = async () => {
      try {
        if (!clientData?.client_id) return;
        const result = await getSettingProfilesByClient(clientData.client_id);
        setProfiles(result);
      } catch (err) {
        toast.error(err.message || "Failed to load setting profiles");
      }
    };

    loadProfiles();
  }, [clientData]);

  const openModal = (profile = null) => {
    setEditingProfile(profile);
    setShowModal(true);
    setShowConfigForm(false);
    if (profile) {
      reset({ name: profile.name });
    } else {
      reset({ name: "" });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProfile(null);
    reset();
    setShowConfigForm(false);
  };

  const handleConfigSubmit = async (config) => {
    try {
      const payload = {
        name:  getValues("name"),
        client_id: clientData.client_id,
        config,
      };

      let result;
      if (editingProfile) {
        result = await updateSettingProfile(editingProfile.profile_id, payload);
        setProfiles((prev) =>
          prev.map((p) => (p.profile_id === result.profile_id ? result : p))
        );
        toast.success("Profile updated successfully");
      } else {
        result = await createSettingProfile(payload);
        setProfiles((prev) => [...prev, result]);
        toast.success("Profile created successfully");
      }

      closeModal();
    } catch (err) {
      toast.error(err.message || "Failed to save profile");
    }
  };

  const deleteProfile = async (profileId) => {
    if (window.confirm("Are you sure you want to delete this profile?")) {
      try {
        const result = await deleteSettingProfile(profileId);
        setProfiles((prev) => prev.filter((p) => p.profile_id !== profileId));
        toast.success(result.message || "Profile deleted");
      } catch (err) {
        toast.error("Delete failed");
      }
    }
  };



  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Manage Setting Profiles</h1>
          <p className="text-gray-600">Create and manage MCU configuration profiles</p>
        </div>
        <button
          onClick={() => openModal()}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <FiPlus className="mr-2" />
          Add Profile
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {profiles.map((profile) => (
          <div
            key={profile.profile_id}
            className="bg-white rounded-xl border shadow-sm p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FiSettings className="text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900">{profile.name}</h3>
              </div>
              <div className="flex items-center space-x-2">
                <button onClick={() => openModal(profile)} className="text-blue-600 hover:text-blue-800">
                  <FiEdit2 />
                </button>
                <button onClick={() => deleteProfile(profile.profile_id)} className="text-red-600 hover:text-red-800">
                  <FiTrash2 />
                </button>
              </div>
            </div>

            {/* Structured settings display instead of a single paragraph */}
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex flex-col justify-between items-start space-y-2">
                <div className="text-black">Irrigation</div>
                <div className="text-right">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">Cycle</span>
                    <span className="inline-block bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                      On {profile.config?.irrigation?.on_cycle_hours ?? "—"}h / Off {profile.config?.irrigation?.off_cycle_hours ?? "—"}h
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-600">Volume</span>
                    <span className="inline-block bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                      {profile.config?.irrigation?.off_cycle_liters ?? "—"} L
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-600">Soil</span>
                    <span className="inline-block bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                      {profile.config?.irrigation?.soil_moisture_on ?? "—"}% — {profile.config?.irrigation?.soil_moisture_off ?? "—"}%
                    </span>
                  </div>
                    <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-600">Alerts</span>
                    <span className="inline-block bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                      {profile.config?.irrigation?.moisture_min_alert ?? "—"}% — {profile.config?.irrigation?.moisture_max_alert ?? "—"}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col justify-between items-start space-y-2">
                <div className="text-black">Ventilation</div>
                <div className="text-right">
                  <span className="inline-block bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                    CO₂ {profile.config?.ventilation?.co2_threshold ?? "—"} ppm
                  </span>
                </div>
                <div className="text-right mt-1">
                  <span className="inline-block bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                    Alerts: {profile.config?.ventilation?.co2_min_alert ?? "—"} ppm — {profile.config?.ventilation?.co2_max_alert ?? "—"} ppm
                  </span>
                </div>
              </div>

              <div className="flex flex-col justify-between items-start space-y-2 ">
                <div className="text-black">Air Conditioning</div>
                <div className="text-right">
                  <div className="flex items-center gap-2">
                    <span className="inline-block bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                      Based on: {profile.config?.ac?.based_on ?? "—"}
                    </span>
                    <span className="inline-block bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                      Target: {profile.config?.ac?.target_temp ?? "—"}°C
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="inline-block bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                      Alert: {profile.config?.ac?.temp_min_alert ?? "—"}°C — {profile.config?.ac?.temp_max_alert ?? "—"}°C
                    </span>
                    </div>

                </div>
              </div>

              <div className="flex flex-col justify-between items-start space-y-2 ">
                <div className="text-black">Humidifier</div>
                <div className="text-right">
                  <div className="inline-block bg-gray-100 text-gray-800 px-2 py-0.5 rounded">
                    On &lt; {profile.config?.humidifier?.humidity_on_below ?? "—"}% • Off &gt; {profile.config?.humidifier?.humidity_off_above ?? "—"}%
                  </div>
                  <div className="inline-block bg-gray-100 text-gray-800 px-2 py-0.5 rounded mt-1">
                    Alerts: {profile.config?.humidifier?.humidity_min_alert ?? "—"}% — {profile.config?.humidifier?.humidity_max_alert ?? "—"}%
                    </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex justify-center items-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
            <button onClick={closeModal} className="absolute top-4 right-4 text-gray-500 hover:text-red-600">
              <FiX />
            </button>
            <h2 className="text-lg font-bold mb-4">
              {editingProfile ? "Edit Profile" : "Add New Profile"}
            </h2>

            {!showConfigForm ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setShowConfigForm(true);
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium">Profile Name</label>
                  <input
                    {...register("name", { required: "Name is required" })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={closeModal} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Next: Define Settings
                  </button>
                </div>
              </form>
            ) : (
              <ControllerSettingsForm
                initialConfig={editingProfile?.config || {}}
                onCancel={closeModal}
                onSubmit={handleConfigSubmit}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSettingProfiles;
